import { avatarDefault } from '@/assets/images';
import { showConfirm, showErrorAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { useCoachQuery } from '@/features/home/queries';
import {
  useCheckSessionForPlayerProgressMutation,
  useUpdatePlayerProgressMutation,
} from '@/features/player-session/queries';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { BadgeName } from '@/features/player-session/types/types';
import { buildPinUrl } from '@/features/player-session/utils';
import { playerKeys } from '@/features/player/queries/query-keys';
import { buildPlayerAvatarSource } from '@/features/player/utils/avatar-uri';
import { ID_TOKEN_REVOKED } from '@/libs/api/interceptors';
import { ApiError } from '@/libs/api/types';
import { HomeStackParamList } from '@/navigations/types';
import { useSessionStore, useUiStore } from '@/stores';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useShallow } from 'zustand/shallow';
import {
  activeBadgeCache,
  activeGroupCache,
  activeStepIdxCache,
  overviewResetStore,
  stepViewModeCache,
} from '../components/overview-session/overview-card-cache-state';
import type { CheckpointItem } from '../components/overview-session/player-session-card.utils';
import {
  buildCheckpointItems,
  buildCheckpointMapping,
  findNextIncompleteSkill,
  getStepPanelBody,
  isSkillAchieved,
  patchProgressAfterCheckpointUnset,
  stateList,
  StepViewMode,
} from '../components/overview-session/player-session-card.utils';

export function usePlayerSessionCard(player: playerSession.SessionLookupData) {
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const qc = useQueryClient();

  const [activeBadge, setActiveBadge] = useState<null | { name: BadgeName; label: string }>(() => {
    const cached = activeBadgeCache.get(player.id);
    if (!cached) return null;
    return { name: cached.name as BadgeName, label: cached.label };
  });
  const [activeGroup, setActiveGroup] = useState<null | { key: string; label: string }>(() => {
    const cached = activeGroupCache.get(player.id);
    return cached ? { key: cached.key, label: cached.label } : null;
  });
  const [activeStepIdx, setActiveStepIdx] = useState<number | null>(
    () => activeStepIdxCache.get(player.id) ?? null,
  );
  const [stepViewMode, setStepViewMode] = useState<StepViewMode>(
    () => stepViewModeCache.get(player.id) ?? StepViewMode.Default,
  );
  const [updatedProgress, setUpdatedProgress] = useState<playerSession.Progress[] | null>(null);
  const [achievedPinUrl, setAchievedPinUrl] = useState<string | null>(null);
  const [levelCompletePinUrl, setLevelCompletePinUrl] = useState<string | null>(null);
  const [displayLevel, setDisplayLevel] = useState<number>(player.level);
  const [pendingNextLevel, setPendingNextLevel] = useState<number | null>(null);
  // When backend returns `newLevel` that skips over already-complete levels, we show certificates sequentially.
  const [pendingNextLevelFinal, setPendingNextLevelFinal] = useState<number | null>(null);
  const [levelOverride, setLevelOverride] = useState<number | null>(null);
  /** Tracks last seen `player.level` from props to detect an authoritative decrease (e.g. Profile edit). */
  const prevPlayerLevelRef = useRef(player.level);

  const resetVersion = useSyncExternalStore(
    overviewResetStore.subscribe,
    overviewResetStore.getSnapshot,
  );

  useEffect(() => {
    // Reset mode when list query changes (search/filter/sort/pagination) so a new list result
    // doesn't inherit an old per-card step state.
    const cachedBadge = activeBadgeCache.get(player.id);
    setActiveBadge(
      cachedBadge ? { name: cachedBadge.name as BadgeName, label: cachedBadge.label } : null,
    );
    const cachedGroup = activeGroupCache.get(player.id);
    setActiveGroup(cachedGroup ? { key: cachedGroup.key, label: cachedGroup.label } : null);
    setActiveStepIdx(activeStepIdxCache.get(player.id) ?? null);
    setStepViewMode(stepViewModeCache.get(player.id) ?? StepViewMode.Default);
  }, [player.id, resetVersion]);

  useEffect(() => {
    activeBadgeCache.set(
      player.id,
      activeBadge ? { name: activeBadge.name, label: activeBadge.label } : null,
    );
  }, [player.id, activeBadge]);

  useEffect(() => {
    activeGroupCache.set(
      player.id,
      activeGroup ? { key: activeGroup.key, label: activeGroup.label } : null,
    );
  }, [player.id, activeGroup]);

  useEffect(() => {
    activeStepIdxCache.set(player.id, activeStepIdx);
  }, [player.id, activeStepIdx]);

  useEffect(() => {
    stepViewModeCache.set(player.id, stepViewMode);
  }, [player.id, stepViewMode]);

  useEffect(() => {
    prevPlayerLevelRef.current = player.level;
    // Intentionally only when switching players — do not sync on every level change (breaks downgrade detection).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- player.level intentionally omitted
  }, [player.id]);

  useEffect(() => {
    const prevPlayerLevel = prevPlayerLevelRef.current;
    const serverLevelDecreased = player.level < prevPlayerLevel;
    prevPlayerLevelRef.current = player.level;

    // Keep in sync with server level unless we're holding a "Level Complete" certificate.
    if (pendingNextLevel !== null) return;
    // If user just pressed NEXT LEVEL, keep showing the override until server catches up.
    if (levelOverride !== null) {
      // NEXT LEVEL sets override above current `player.level` until refetch — that is NOT a downgrade.
      // Only clear override when server level actually dropped vs. last props (Profile / list caught up).
      if (player.level < levelOverride && serverLevelDecreased) {
        setLevelOverride(null);
        setDisplayLevel(player.level);
        setUpdatedProgress(null);
        return;
      }
      if (player.level >= levelOverride) {
        setLevelOverride(null);
        setDisplayLevel(player.level);
      } else {
        setDisplayLevel(levelOverride);
      }
      return;
    }

    // Normal mode: always reflect server level (including level decreases from Profile edits).
    if (displayLevel !== player.level) {
      setDisplayLevel(player.level);
      setUpdatedProgress(null);
    }
  }, [displayLevel, levelOverride, pendingNextLevel, player.level]);

  // When `player.progress` changes via an external cache patch (e.g. clearing progress
  // from the Mutate Player screen), drop any local optimistic override so the fresh
  // prop value takes effect. Safe for local mutations too: they patch the list cache to
  // the same array reference they store in `updatedProgress`, so rendering `player.progress`
  // produces identical output.
  useEffect(() => {
    setUpdatedProgress(null);
  }, [player.progress]);

  const userUid = useSessionStore(s => s.user?.uid);
  const { data: coach } = useCoachQuery(userUid);
  const sessionId = coach?.active_session_id;
  const updatePlayer = useUpdatePlayerProgressMutation();
  const checkSession = useCheckSessionForPlayerProgressMutation();
  const isUpdatingProgress = updatePlayer.isPending;

  const progress = (updatedProgress ?? player.progress)?.[displayLevel - 1];

  const activeBadgeData = useMemo(() => {
    if (!activeBadge) return undefined;
    return progress?.badges.find(badge => badge.name === activeBadge.name);
  }, [activeBadge, progress?.badges]);

  const subtitle = useMemo(() => {
    const levelPrefix = `Level ${displayLevel}:`;
    if (!activeBadge) return `${levelPrefix} Overview`;
    if (!activeGroup) return `${levelPrefix} ${activeBadge.label}`;
    return `${levelPrefix} ${activeGroup.label}`;
  }, [activeBadge, activeGroup, displayLevel]);

  const isCheckpointMode = Boolean(activeBadge);

  const skillAchieved = useMemo(() => {
    if (!activeBadge) return false;
    const badge = progress?.badges.find(b => b.name === activeBadge.name);
    return isSkillAchieved(badge);
  }, [activeBadge, progress?.badges]);

  const checkpointItems = useMemo(
    () =>
      buildCheckpointItems({
        activeBadge,
        activeBadgeData,
        activeGroup,
        onSelectGroup: setActiveGroup,
      }),
    [activeBadge, activeBadgeData, activeGroup],
  );

  const stepCandidates = useMemo(
    () => checkpointItems.filter(i => i.hideProgress),
    [checkpointItems],
  );

  const activeStep = useMemo(() => {
    if (activeStepIdx === null) return null;
    return stepCandidates[activeStepIdx] ?? null;
  }, [activeStepIdx, stepCandidates]);

  const activeStepLabel = activeStep?.label ?? '';
  const resolvedInstruction = useMemo(
    () => getStepPanelBody(stepViewMode, displayLevel, activeStepLabel),
    [activeStepLabel, displayLevel, stepViewMode],
  );

  const avatarSource =
    buildPlayerAvatarSource({
      playerId: player.id,
      avatarUrl: player.avatar_url,
    }) ?? avatarDefault;

  const onPressProfile = () => {
    navigation.navigate('PlayerProfile', {
      playerId: player.id,
      mutateDeleteReturnTo: 'OverviewSession',
    });
  };

  const onPressReturn = () => {
    // If we're in step detail, "Return" should go back to the step list (keep badge/group).
    if (activeStepIdx !== null) {
      setActiveStepIdx(null);
      setStepViewMode(StepViewMode.Default);
      return;
    }
    if (activeGroup) setActiveGroup(null);
    else setActiveBadge(null);
  };

  const onPrevStep = () => setActiveStepIdx(i => (i === null ? i : Math.max(0, i - 1)));
  const onNextStep = () =>
    setActiveStepIdx(i => (i === null ? i : Math.min(stepCandidates.length - 1, i + 1)));

  const onCompleteStep = () => {
    if (!sessionId) {
      showErrorAlert('Missing sessionId');
      return;
    }
    if (!activeBadge || !activeStep) return;
    const checkpointMapping = buildCheckpointMapping({
      level: displayLevel,
      badgeName: activeBadge.name,
      checkpointName: activeStep.key,
    });

    showGlobalLoading();
    updatePlayer.mutate(
      {
        sessionId,
        playerId: player.id,
        checkpointMappings: checkpointMapping,
        status: 'completed',
      },
      {
        onSuccess: data => {
          const nextProgress = Array.isArray(data?.progress) ? (data.progress as any) : null;
          if (nextProgress) setUpdatedProgress(nextProgress);
          const pins = Array.isArray((data as any)?.pins) ? ((data as any).pins as any[]) : [];
          const firstPinPic = pins?.[0]?.pinPicture ?? null;

          setAchievedPinUrl(buildPinUrl(firstPinPic ?? undefined));

          // Determine the next step based on the UPDATED progress, not the stale memoized stepCandidates.
          if (!nextProgress) {
            // Fallback: keep current behavior.
            const nextIdx = (() => {
              const next = stepCandidates.findIndex(
                (s, idx) => idx > (activeStepIdx ?? -1) && s.data?.badgeEarned !== true,
              );
              if (next !== -1) return next;
              const first = stepCandidates.findIndex(s => s.data?.badgeEarned !== true);
              return first === -1 ? null : first;
            })();
            setActiveStepIdx(nextIdx);
            setStepViewMode(nextIdx === null ? StepViewMode.Achieved : StepViewMode.Default);
            return;
          }

          // Use displayLevel (UI level) rather than player.level (prop may lag).
          const nextLevelProgress = nextProgress?.[displayLevel - 1];
          const nextBadgeData = activeBadge
            ? nextLevelProgress?.badges?.find((b: any) => b?.name === activeBadge.name)
            : undefined;
          const nextItems = buildCheckpointItems({
            activeBadge,
            activeBadgeData: nextBadgeData,
            activeGroup,
            onSelectGroup: () => {},
          });
          const nextCandidates = nextItems.filter(i => i.hideProgress);
          const currentKey = activeStep?.key ?? null;
          const currentIdx = currentKey ? nextCandidates.findIndex(c => c.key === currentKey) : -1;

          const nextIncompleteIdx = (() => {
            const after = nextCandidates.findIndex(
              (c, idx) => idx > currentIdx && c.data?.badgeEarned !== true,
            );
            if (after !== -1) return after;
            const first = nextCandidates.findIndex(c => c.data?.badgeEarned !== true);
            return first === -1 ? null : first;
          })();

          if (nextIncompleteIdx === null) {
            // Skill achieved; if this was the last incomplete skill of the level, show Level Complete certificate.
            const nextSkill = activeBadge
              ? findNextIncompleteSkill({
                  progress: nextLevelProgress,
                  fromBadge: activeBadge.name,
                })
              : null;

            const levelIsComplete = stateList.every(s => {
              const b = nextLevelProgress?.badges?.find((bb: any) => bb?.name === s.name);
              return isSkillAchieved(b);
            });

            const newLevel = (data as any)?.newLevel ?? null;
            if (levelIsComplete && typeof newLevel === 'number') {
              const overallPin =
                pins.find(p => String(p?.name ?? '').toLowerCase() === 'overall') ??
                pins.find(p =>
                  String(p?.pinPicture ?? '')
                    .toLowerCase()
                    .includes('_overall'),
                );

              const fallbackOverallPic = `LV${displayLevel}_Overall.png`;
              setLevelCompletePinUrl(
                buildPinUrl(overallPin?.pinPicture ?? fallbackOverallPic) ?? null,
              );
              // If backend skips ahead (e.g. current=7, newLevel=9 because level 8 already complete),
              // show "Level 8 Complete" certificate before advancing to 9.
              const immediateNext = displayLevel + 1;
              if (newLevel > immediateNext) {
                setPendingNextLevel(immediateNext);
                setPendingNextLevelFinal(newLevel);
              } else {
                setPendingNextLevel(newLevel);
                setPendingNextLevelFinal(null);
              }
              setActiveStepIdx(null);
              // iOS flow: show Skill Achieved first; user taps NEXT SKILL to reveal Level Complete certificate.
              setStepViewMode(StepViewMode.Achieved);
              return;
            }

            // Otherwise, show Skill Achieved panel as before.
            setActiveStepIdx(null);
            setStepViewMode(StepViewMode.Achieved);
            // If there is no next skill, leaving Achieved will return to overview.
            if (!nextSkill) {
              // keep state; NEXT SKILL will handle null by returning to overview
            }
          } else {
            setActiveStepIdx(nextIncompleteIdx);
            setStepViewMode(StepViewMode.Default);
          }
        },
        onError: err => {
          if (
            (err as any as ApiError)?.serverCode === ID_TOKEN_REVOKED &&
            (err as any as ApiError)?.status === 401
          ) {
            return;
          }
          if (shouldSuppressErrorAlert(err)) {
            return;
          }
          showErrorAlert(String(STRINGS.errors.firestoreTimeout));
        },
        onSettled: () => {
          hideGlobalLoading();
        },
      },
    );
  };

  const onIncompleteStep = () => {
    if (!sessionId) {
      showErrorAlert('Missing sessionId');
      return;
    }
    if (!activeBadge || !activeStep) {
      setStepViewMode(StepViewMode.Incomplete);
      return;
    }
    const checkpointMapping = buildCheckpointMapping({
      level: displayLevel,
      badgeName: activeBadge.name,
      checkpointName: activeStep.key,
    });

    showGlobalLoading();
    updatePlayer.mutate(
      {
        sessionId,
        playerId: player.id,
        checkpointMappings: checkpointMapping,
        status: 'attempted',
      },
      {
        onSuccess: data => {
          if (Array.isArray(data?.progress)) setUpdatedProgress(data.progress as any);
          setStepViewMode(StepViewMode.Incomplete);
        },
        onError: err => {
          if (
            (err as any as ApiError)?.serverCode === ID_TOKEN_REVOKED &&
            (err as any as ApiError)?.status === 401
          ) {
            return;
          }
          if (shouldSuppressErrorAlert(err)) {
            return;
          }
          showErrorAlert(String(STRINGS.errors.firestoreTimeout));
        },
        onSettled: () => {
          hideGlobalLoading();
        },
      },
    );
  };

  const onTryAgain = () => setStepViewMode(StepViewMode.Default);
  const onSkip = () => {
    setStepViewMode(StepViewMode.Default);
    setActiveStepIdx(null);
  };

  const onNextSkill = () => {
    // If we already determined the level is complete, NEXT SKILL should reveal the certificate.
    if (pendingNextLevel !== null) {
      setActiveStepIdx(null);
      setStepViewMode(StepViewMode.LevelComplete);
      setActiveGroup(null);
      setAchievedPinUrl(null);
      setActiveBadge(null);
      return;
    }
    // UX: "NEXT SKILL" should return to the skill list (overview),
    // not auto-navigate to the next skill card.
    setActiveStepIdx(null);
    setStepViewMode(StepViewMode.Default);
    setActiveGroup(null);
    setAchievedPinUrl(null);
    setActiveBadge(null);
  };

  const onNextLevel = () => {
    if (pendingNextLevel === null) return;
    // If we are in a "skipped levels" chain, show the next level's certificate first.
    // Example: displayLevel=7, pendingNextLevel=8, pendingNextLevelFinal=9
    // Press NEXT LEVEL => show Level 8 Complete certificate, then next press advances to 9.
    if (pendingNextLevelFinal !== null && pendingNextLevelFinal > pendingNextLevel) {
      const nextCertLevel = pendingNextLevel;
      const fallbackOverallPic = `LV${nextCertLevel}_Overall.png`;
      setDisplayLevel(nextCertLevel);
      setLevelCompletePinUrl(buildPinUrl(fallbackOverallPic) ?? null);
      setPendingNextLevel(pendingNextLevelFinal);
      setPendingNextLevelFinal(null);
      setActiveBadge(null);
      setActiveGroup(null);
      setActiveStepIdx(null);
      setStepViewMode(StepViewMode.LevelComplete);
      setAchievedPinUrl(null);
      return;
    }

    // Normal: advance to the next playable level.
    setDisplayLevel(pendingNextLevel);
    setLevelOverride(pendingNextLevel);
    setPendingNextLevel(null);
    setPendingNextLevelFinal(null);
    setActiveBadge(null);
    setActiveGroup(null);
    setActiveStepIdx(null);
    setStepViewMode(StepViewMode.Default);
    setAchievedPinUrl(null);
    setLevelCompletePinUrl(null);

    // Ensure Player Profile refetches fresh level/progress (avoid stale cache).
    qc.invalidateQueries({ queryKey: playerKeys.profile(player.id) });
  };

  /** Level 10 complete: no next level in app — return to player skill overview. */
  const onBackFromLevelComplete = () => {
    setPendingNextLevel(null);
    setPendingNextLevelFinal(null);
    setActiveBadge(null);
    setActiveGroup(null);
    setActiveStepIdx(null);
    setStepViewMode(StepViewMode.Default);
    setAchievedPinUrl(null);
    setLevelCompletePinUrl(null);
    qc.invalidateQueries({ queryKey: playerKeys.profile(player.id) });
  };

  const onPressCheckpointItem = (item: CheckpointItem) => {
    if (item.onPress) {
      setActiveStepIdx(null);
      setStepViewMode(StepViewMode.Default);
      item.onPress();
      return;
    }
    if (!item.hideProgress) return;

    const completed = item.data?.badgeEarned === true;
    if (completed && activeBadge) {
      if (!sessionId) {
        showErrorAlert('Missing sessionId');
        return;
      }
      const checkpointMapping = buildCheckpointMapping({
        level: displayLevel,
        badgeName: activeBadge.name,
        checkpointName: item.key,
      });

      let proceedToUnset = false;

      const doUnset = () => {
        proceedToUnset = true;
        updatePlayer.mutate(
          {
            sessionId,
            playerId: player.id,
            checkpointMappings: checkpointMapping,
            status: 'unattempted',
          },
          {
            onSuccess: data => {
              const resp = data as playerSession.UpdatePlayerProgressResponse | undefined;
              const raw = resp?.progress;
              const base = updatedProgress ?? player.progress;
              const nextProgress =
                Array.isArray(raw) && raw.length > 0
                  ? (raw as playerSession.Progress[])
                  : base?.length && activeBadge
                    ? patchProgressAfterCheckpointUnset({
                        progress: base,
                        levelIndex: displayLevel - 1,
                        badgeName: activeBadge.name,
                        checkpointKey: item.key,
                      })
                    : null;

              if (nextProgress) {
                setUpdatedProgress(nextProgress);
                // Avoid refetch + list overlay while global loading is visible.
                qc.setQueriesData({ queryKey: [...playerSessionKeys.all, 'list'] }, (old: any) => {
                  if (!old?.data || !Array.isArray(old.data)) return old;
                  return {
                    ...old,
                    data: old.data.map((p: any) =>
                      p?.id === player.id ? { ...p, progress: nextProgress } : p,
                    ),
                  };
                });
              }
              setStepViewMode(StepViewMode.Default);
              setActiveStepIdx(null);
            },
            onError: err => {
              if (
                (err as any as ApiError)?.serverCode === ID_TOKEN_REVOKED &&
                (err as any as ApiError)?.status === 401
              ) {
                return;
              }
              if (shouldSuppressErrorAlert(err)) {
                return;
              }
              const msg =
                (err as any)?.message ??
                STRINGS.common.alert?.error ??
                'Something went wrong. Please try again.';
              showErrorAlert(String(msg));
            },
            onSettled: () => {
              hideGlobalLoading();
            },
          },
        );
      };

      showGlobalLoading();
      checkSession.mutate(
        { sessionId, playerId: player.id, checkpointMapping },
        {
          onSuccess: data => {
            if (data?.isInSession === true) {
              doUnset();
              return;
            }
            // Let user interact with the alert; do not keep global loading visible.
            hideGlobalLoading();
            showConfirm({
              title: STRINGS.playerSession.stepDetail.unsetAlertTitle,
              message: STRINGS.playerSession.stepDetail.unsetAlertBody,
              cancelText: STRINGS.playerSession.stepDetail.cancel,
              confirmText: STRINGS.playerSession.stepDetail.unset,
              confirmStyle: 'destructive',
            }).then(confirmed => {
              if (confirmed) {
                doUnset();
              }
            });
          },
          onError: err => {
            if (
              (err as any as ApiError)?.serverCode === ID_TOKEN_REVOKED &&
              (err as any as ApiError)?.status === 401
            ) {
              return;
            }
            if (shouldSuppressErrorAlert(err)) {
              hideGlobalLoading();
              return;
            }
            hideGlobalLoading();
            const msg =
              (err as any)?.message ??
              STRINGS.common.alert?.error ??
              'Something went wrong. Please try again.';
            showErrorAlert(String(msg));
          },
          onSettled: () => {
            // If doUnset runs, updatePlayer will manage loading; otherwise hide here.
            if (!proceedToUnset) {
              hideGlobalLoading();
            }
          },
        },
      );
      return;
    }

    setStepViewMode(StepViewMode.Default);
    const idx = stepCandidates.findIndex(s => s.key === item.key);
    setActiveStepIdx(idx === -1 ? 0 : idx);
  };

  const onPressStateSkill = (badge: { name: BadgeName; label: string }) => {
    setActiveStepIdx(null);
    setStepViewMode(StepViewMode.Default);
    setActiveGroup(null);
    setActiveBadge(badge);
  };

  return {
    // state
    activeBadge,
    activeGroup,
    activeStepIdx,
    stepViewMode,

    // data
    progress,
    avatarSource,
    subtitle,
    isCheckpointMode,
    skillAchieved,
    checkpointItems,
    stepCandidates,
    activeStep,
    activeStepLabel,
    resolvedInstruction,
    achievedPinUrl,
    levelCompletePinUrl,
    isUpdatingProgress,
    displayLevel,
    pendingNextLevel,
    pendingNextLevelFinal,

    // handlers
    onPressProfile,
    onPressReturn,
    onPrevStep,
    onNextStep,
    onCompleteStep,
    onIncompleteStep,
    onTryAgain,
    onSkip,
    onNextSkill,
    onNextLevel,
    onBackFromLevelComplete,
    onPressCheckpointItem,
    onPressStateSkill,
  };
}
